import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import styles from "./VennioVibeCoders.module.css";

// ─── Data ─────────────────────────────────────────────────────

const PROBLEM_CARDS = [
  {
    num: "01",
    title: "They forget to book",
    body: "You sent the link. They meant to schedule. Life happened.",
  },
  {
    num: "02",
    title: "You lose deals waiting",
    body: "Every day without a meeting is a day they're thinking about your competition.",
  },
  {
    num: "03",
    title: "Back-and-forth emails",
    body: "Six messages to agree on a time, then one of you reschedules.",
  },
];

const INVERSION_STEPS = [
  "Contact shares their calendar — one-time, 30 seconds.",
  "You see their free slots in real time.",
  "You propose a time — they confirm in one tap.",
];

const ICEBERG_ITEMS = [
  { label: "Your booking UI", above: true },
  { label: "OAuth token management", above: false },
  { label: "Google + Microsoft calendar sync", above: false },
  { label: "Timezone handling", above: false },
  { label: "Conflict detection", above: false },
  { label: "Webhook delivery + retries", above: false },
  { label: "GDPR consent tracking", above: false },
  { label: "CRM auto-sync (HubSpot, Salesforce)", above: false },
  { label: "Email confirmations", above: false },
  { label: "Rate limiting + security", above: false },
];

const API_CODE = `// 1. See when your contact is free
const { blocks } = await fetch(
  'https://api.vennio.app/v1/principals/{principalId}/availability',
  { headers: { Authorization: 'Bearer vennio_sk_live_...' } }
).then(r => r.json());

// 2. Propose a time
const { proposal } = await fetch('https://api.vennio.app/v1/proposals', {
  method: 'POST',
  headers: {
    Authorization: 'Bearer vennio_sk_live_...',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    recipient_email: 'jane@example.com',
    time_slots: [{ start: blocks[0].start, end: blocks[0].end }]
  })
}).then(r => r.json());

// 3. Webhook fires when they respond
// POST to your URL → { event: 'booking.created', booking }`;

const STATS = [
  { value: "85+", label: "API Endpoints" },
  { value: "2",   label: "Calendar Providers" },
  { value: "<5min", label: "Time to First Booking" },
];

// ─── Icon ─────────────────────────────────────────────────────

function Icon({ name, size = 20, color = "currentColor" }) {
  const s = { width: size, height: size, color, display: "inline-block", flexShrink: 0 };
  const icons = {
    copy: (
      <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
      </svg>
    ),
    check: (
      <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    ),
    "arrow-right": (
      <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
      </svg>
    ),
    sparkles: (
      <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"/>
      </svg>
    ),
  };
  return icons[name] || null;
}

// ─── CopyButton ────────────────────────────────────────────────

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className={`${styles.copyBtn}${copied ? ` ${styles.copyBtnCopied}` : ""}`}
    >
      <Icon name={copied ? "check" : "copy"} size={14} />
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

// ─── CodeBlock ────────────────────────────────────────────────

function CodeBlock({ code }) {
  return (
    <div className={styles.codeBlockWrap}>
      <div className={styles.codeBlockInner}>
        <CopyButton text={code} />
        <pre className={styles.codeBlockPre}>{code}</pre>
      </div>
    </div>
  );
}

// ─── Counter ──────────────────────────────────────────────────

function Counter({ value, label }) {
  const [display, setDisplay] = useState("0");
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const numMatch = value.match(/(\d+)/);
    if (!numMatch) { setDisplay(value); return; }
    const target = parseInt(numMatch[1]);
    const prefix = value.slice(0, value.indexOf(numMatch[1]));
    const suffix = value.slice(value.indexOf(numMatch[1]) + numMatch[1].length);
    let current = 0;
    const step = Math.max(1, Math.floor(target / 40));
    const interval = setInterval(() => {
      current = Math.min(current + step, target);
      setDisplay(`${prefix}${current}${suffix}`);
      if (current >= target) clearInterval(interval);
    }, 30);
    return () => clearInterval(interval);
  }, [visible, value]);

  return (
    <div ref={ref} className={styles.counter}>
      <div className={styles.counterValue}>{display}</div>
      <div className={styles.counterLabel}>{label}</div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────

export default function VennioVibeCoders() {
  return (
    <div className={styles.page}>

      {/* ─── Nav ─── */}
      <nav className={styles.nav}>
        <div className={styles.navInner}>
          <div className={styles.navLogo}>
            <div className={styles.navBadge}>V</div>
            <span className={styles.navBrand}>vennio</span>
          </div>
          <div className={styles.navLinks}>
            <a href="#how-it-works" className={styles.navLink}>How It Works</a>
            <a href="https://docs.vennio.app" className={styles.navLink} target="_blank" rel="noopener noreferrer">For Developers</a>
            <Link to="/auth" className={styles.navLink}>Sign Up</Link>
            <Link to="/auth" className="btn btn-inverse">Get Started</Link>
          </div>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className={styles.hero}>
        <div className={styles.heroPill}>
          <span className="pill pill-accent">
            <Icon name="sparkles" size={12} />
            {" "}Scheduling API
          </span>
        </div>
        <h1 className={styles.heroTitle}>
          Stop waiting for clients to book.<br />
          <span className={styles.heroTitleAmber}>See when they're free.</span>
        </h1>
        <p className={styles.heroBody}>
          Most scheduling tools make your customers do the work — pick a slot, hope it still
          works tomorrow. Vennio flips it: you see when they're free and propose a time.
          They confirm in one tap.
        </p>
        <div className={styles.heroActions}>
          <Link to="/auth" className={styles.ctaPrimary}>
            Get Started Free <Icon name="arrow-right" size={16} />
          </Link>
          <a href="https://docs.vennio.app" className={styles.ctaSecondary} target="_blank" rel="noopener noreferrer">
            Read the Docs
          </a>
        </div>

        <div className={styles.terminal}>
          <div className={styles.terminalBar}>
            <div className={styles.terminalDotRed} />
            <div className={styles.terminalDotYellow} />
            <div className={styles.terminalDotGreen} />
            <span className={styles.terminalLabel}>vennio-api</span>
          </div>
          <div className={styles.terminalBody}>
            <div className={styles.terminalLine}>
              <span className={styles.terminalArrow}>→</span> Checking Jane's calendar...
            </div>
            <div className={`${styles.terminalLine} ${styles.terminalSuccess}`}>
              ✓ Jane is free Tuesday 2pm–4pm
            </div>
            <div className={`${styles.terminalLine} ${styles.terminalSuccess}`}>
              ✓ Thursday 10am–12pm
            </div>
            <div className={styles.terminalLine}>
              <span className={styles.terminalArrow}>→</span> Proposing Tuesday 2pm...
            </div>
            <div className={`${styles.terminalLine} ${styles.terminalSuccess}`}>
              ✓ Booking confirmed — no back-and-forth
            </div>
          </div>
        </div>
      </section>

      {/* ─── The Problem ─── */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <p className="section-label">The Problem</p>
          <h2 className={styles.sectionTitle}>If they have to pick a time, some of them won't.</h2>
          <div className={styles.problemGrid}>
            {PROBLEM_CARDS.map((card) => (
              <div key={card.num} className={styles.problemCard}>
                <p className={styles.problemNum}>{card.num}</p>
                <h3 className={styles.problemTitle}>{card.title}</h3>
                <p className={styles.problemBody}>{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── The Inversion ─── */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.inversionCard}>
            <div className={styles.inversionBadge}>
              <span className="pill pill-accent">
                <Icon name="sparkles" size={12} />
                {" "}What makes Vennio different
              </span>
            </div>
            <h2 className={styles.inversionTitle}>
              Most tools show your availability.{" "}
              <span className={styles.inversionTitleAmber}>Vennio shows you theirs.</span>
            </h2>
            <p className={styles.inversionBody}>
              Traditional scheduling is supply-side: you publish your calendar, customers pick a time.
              Vennio inverts this. You see when your contacts are free and proactively propose a meeting.
              They confirm in one tap.
            </p>
            <div className={styles.inversionCompare}>
              <div className={styles.compareOld}>
                <p className={styles.compareLabel}>Calendly / Cal.com</p>
                <p className={styles.compareQuote}>"Here's my calendar. Pick a time."</p>
                <p className={styles.compareNote}>→ Customer does the work</p>
              </div>
              <div className={styles.compareNew}>
                <p className={styles.compareLabel}>Vennio</p>
                <p className={styles.compareQuote}>"I can see you're free Tuesday at 2pm. Want to meet?"</p>
                <p className={styles.compareNote}>→ You propose. They confirm.</p>
              </div>
            </div>
            <div className={styles.steps}>
              {INVERSION_STEPS.map((text, i) => (
                <div key={i} className={styles.step}>
                  <span className={styles.stepNum}>{i + 1}</span>
                  <span className={styles.stepText}>{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── The Iceberg ─── */}
      <section id="how-it-works" className={styles.section}>
        <div className={styles.sectionInner}>
          <p className="section-label">What Vennio handles</p>
          <h2 className={styles.sectionTitle}>You build the UI. We handle everything else.</h2>
          <p className={styles.sectionBody}>
            Scheduling looks simple on the surface. Under the hood it's OAuth flows, token refresh,
            timezone math, conflict detection, and a dozen other things you don't want to build.
          </p>
          <div className={styles.iceberg}>
            {ICEBERG_ITEMS.map((item, i) => (
              <div
                key={i}
                className={[
                  styles.icebergRow,
                  item.above ? styles.icebergRowAbove : "",
                  i === 0 ? styles.icebergRowBoundary : "",
                ].filter(Boolean).join(" ")}
              >
                {item.above ? (
                  <span className={styles.icebergBadgeYou}>You</span>
                ) : (
                  <span className={styles.icebergBadgeVennio}>Vennio</span>
                )}
                <span className={styles.icebergLabel}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── API in 3 Calls ─── */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <p className="section-label">For Developers</p>
          <h2 className={styles.sectionTitle}>Add scheduling to any product in 3 API calls.</h2>
          <p className={styles.sectionBody}>
            No OAuth headaches. No calendar sync logic. No timezone nightmares.
            Just an API key and these three calls.
          </p>
          <CodeBlock code={API_CODE} />
          <div className={styles.docsLink}>
            <a
              href="https://docs.vennio.app"
              className={styles.docsLinkText}
              target="_blank"
              rel="noopener noreferrer"
            >
              Read the full API docs <Icon name="arrow-right" size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* ─── Stats ─── */}
      <div className={styles.statsSection}>
        <div className={styles.statsGrid}>
          {STATS.map((s) => (
            <Counter key={s.label} value={s.value} label={s.label} />
          ))}
        </div>
      </div>

      {/* ─── CTA ─── */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <h2 className={styles.ctaTitle}>Ready to see when they're free?</h2>
          <p className={styles.ctaBody}>
            Create your account in minutes. Connect your API key.
            Your customers never have to pick a time again.
          </p>
          <div className={styles.ctaActions}>
            <Link to="/auth" className={styles.ctaPrimary}>
              Create Free Account <Icon name="arrow-right" size={16} />
            </Link>
            <a
              href="https://docs.vennio.app"
              className={styles.ctaSecondary}
              target="_blank"
              rel="noopener noreferrer"
            >
              Explore the API
            </a>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <span className={styles.footerText}>
            Vennio — Infrastructure for Time. Built in Newcastle upon Tyne.
          </span>
          <div className={styles.footerLinks}>
            <a href="https://github.com/vennio" className={styles.footerLink} target="_blank" rel="noopener noreferrer">GitHub</a>
            <a href="https://app.swaggerhub.com/apis/whisker1/vennio-api/1.1.0" className={styles.footerLink} target="_blank" rel="noopener noreferrer">Swagger</a>
            <a href="https://vennio.app" className={styles.footerLink}>Main Site</a>
          </div>
        </div>
      </footer>

    </div>
  );
}
